import type { components } from './api.types'

/**
 * The backend's error envelope, and the codes worth branching on.
 *
 * **It is GENERATED now.** It was the one hand-written type in a codebase whose
 * whole §4.3 point is that types come from the contract — not as a shortcut, but
 * because `brainspot/contracts/problem.py` was returned from FastAPI exception
 * handlers as a bare `JSONResponse` and never declared as a response model, so it
 * never reached `components.schemas` and `openapi-typescript` had nothing to
 * emit. Backend follow-up 13 closed that on 2026-09-12: `PROBLEM_RESPONSES` is
 * declared on every gated router, which is documentation only — FastAPI's
 * `responses` argument does not validate, coerce or change what a handler
 * returns, and a backend test asserts no response body moved.
 *
 * What stays hand-written is everything BELOW this line: the codes, the account
 * statuses and the typed sentinels. Those are behaviour, not shape, and the
 * document cannot express them.
 *
 * Two things about the wire format that a client gets wrong by default:
 *
 *   1. It is served as **`application/json`**, not `application/problem+json`
 *      (`problem.py`, a bare `JSONResponse`) — and the document now says so
 *      explicitly rather than by omission. Sniffing the content-type finds
 *      nothing. Detect a Problem by the presence of a string `code`.
 *
 *   2. `code` is **not** always the class default. `AccountNotApprovedError`
 *      rebuilds it per instance as `f"account.{account_status}"`
 *      (`errors.py:277`), so five distinct codes reach the client — one per
 *      non-active status — where the class declares only `account.not_approved`.
 *      Branch on the code; never on `detail`, which exists to be human-readable
 *      and is therefore the one field free to change.
 */

/**
 * `brainspot/contracts/problem.py::Problem`, from the contract.
 *
 * Re-exported under this name rather than used as
 * `components['schemas']['Problem']` everywhere: about thirty call sites import
 * `Problem`, and the generated shape is now the single source of truth for all
 * of them. If the backend changes a field, `tsc` says so here.
 */
export type Problem = components['schemas']['Problem']

/**
 * Account statuses, from `brainspot/contracts/user.py::UserStatus`.
 * Mirrored here as a value so the screens can exhaustively switch on it; the
 * generated `api.types.ts` carries the same union as a type, and
 * `problem.test.ts` asserts the two agree.
 */
export const ACCOUNT_STATUSES = [
  'pending_approval',
  'active',
  'suspended',
  'rejected',
  'deleted',
  'purged',
] as const

export type AccountStatus = (typeof ACCOUNT_STATUSES)[number]

/** The 403 code the gate emits for each non-active status. */
export type AccountProblemCode = `account.${AccountStatus}`

export function isProblem(value: unknown): value is Problem {
  if (!value || typeof value !== 'object') return false
  const p = value as Partial<Problem>
  return typeof p.code === 'string' && typeof p.status === 'number'
}

/**
 * The account status a 403 is complaining about, or null if it is a different
 * 403 — a non-admin reaching an admin route emits `auth.forbidden`, and that
 * needs a different screen.
 */
export function accountStatusFromProblem(problem: Problem): AccountStatus | null {
  if (!problem.code.startsWith('account.')) return null
  const status = problem.code.slice('account.'.length)
  return (ACCOUNT_STATUSES as readonly string[]).includes(status)
    ? (status as AccountStatus)
    : null
}

/* ── Typed sentinels ─────────────────────────────────────────────────────────
 *
 * `UnauthorizedError` and `ForbiddenError` are AIVY's shape. `ProblemError` is
 * NOT a port: Sprint 1.8 §4.1 lists it among the sentinels to lift, but it does
 * not exist in the AIVY frontend at any revision. It is net-new here, and it is
 * what makes code-branching possible at all.
 */

export class ApiError extends Error {
  readonly status: number
  readonly problem: Problem | null

  constructor(message: string, status: number, problem: Problem | null = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }

  /** The machine-readable code, when the body carried one. */
  get code(): string | null {
    return this.problem?.code ?? null
  }
}

/** 401. The credentials are absent, expired or rejected; signing in again helps. */
export class UnauthorizedError extends ApiError {
  constructor(problem: Problem | null = null) {
    super(problem?.detail ?? 'Your sign-in has expired.', 401, problem)
    this.name = 'UnauthorizedError'
  }
}

/**
 * 403. The credentials were fine and re-authenticating will not help — which is
 * exactly why this is a different class from `UnauthorizedError` and must never
 * trigger a sign-out.
 */
export class ForbiddenError extends ApiError {
  constructor(problem: Problem | null = null) {
    super(problem?.detail ?? 'You do not have access to that.', 403, problem)
    this.name = 'ForbiddenError'
  }

  /** Non-null when the refusal is about account state rather than permission. */
  get accountStatus(): AccountStatus | null {
    return this.problem ? accountStatusFromProblem(this.problem) : null
  }
}

/** Any other Problem-shaped failure. */
export class ProblemError extends ApiError {
  constructor(problem: Problem) {
    super(problem.detail || problem.title, problem.status, problem)
    this.name = 'ProblemError'
  }
}

/**
 * A 404 that means "there is nothing here", which is rarely a fault.
 *
 * Both callers read it as an explanatory state rather than an error: the
 * tracking routes answer 404 for a condition with no history, and a recording
 * by id answers 404 for one that is absent **or foreign** — the backend builds
 * those two identically on purpose (`application/recordings.py:64-72`), because
 * `recording_id` is timestamp-derived and a 403 would confirm existence.
 *
 * Reads `.problem` rather than `.status` so a non-`ApiError` rejection — a
 * transport failure, an abort — can never be mistaken for a clean miss.
 */
export function isNotFound(error: unknown): boolean {
  const problem = (error as { problem?: unknown } | null)?.problem
  return isProblem(problem) && problem.status === 404
}

export const isUnauthorized = (e: unknown): e is UnauthorizedError => e instanceof UnauthorizedError
export const isForbidden = (e: unknown): e is ForbiddenError => e instanceof ForbiddenError
